/**
 * Frontend entry point.
 *
 * Routing: History API, clean URLs — /login, /register, /forgot-password,
 * /reset-password?token=..., /verify-email?token=..., /play (authenticated).
 *
 * Game developers wire their rendering engine inside mountGame() — replace
 * the placeholder with PixiJS, Babylon.js, or DOM rendering.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { setOrientationGate } from './engine/mobile/orientation';
import { renderLogin } from './core/auth/login-screen';
import { renderRegister } from './core/auth/register-screen';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { renderLegal } from './core/legal/legal-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
import { mountGame, unmountGame } from './engine/bootstrap';
import { loadGameFonts } from './engine/fonts';
import { attachAuthBack } from './engine/auth-back';
import { renderCharacterCreator } from './engine/character/creator';
import { loadAppearance, saveAppearance } from './engine/character/appearance-store';
import { syncAssignedHero } from './engine/character/assigned-hero';
import { accountApi } from './engine/game-api';
import { renderLanding } from './engine/landing/landing';
import { legacyArticleRedirect, parseArticleRoute, renderArticlesPage } from './engine/landing/articles';
import { setTutorialAccount } from './engine/tutorial/state';
import { isBrowserHandledLink } from './engine/link-router';
import { refreshSessionUser, verifyDecision } from './engine/verify-gate';
import { enforceTabSession, markTabSession, startIdleWatch, stopIdleWatch } from './engine/session-policy';
import { writeLastTab } from './engine/bootstrap';
import { toast } from './engine/dom';

/**
 * Routes reached by clicking a link in an email, carrying a one-time token.
 *
 * They must RENDER whatever the cookie jar and the tab rule say, because the
 * screen is what spends the token. See the boot block for the bug this exists to
 * stop: `enforceTabSession()` returns before `render()`, and a verification link
 * opened in a new tab — which is what every mail client does — therefore signed
 * the player out WITHOUT EVER SENDING THE VERIFICATION. The link did nothing, and
 * clicking it again did nothing again, because a fresh tab never owns the session.
 */
const EMAIL_TOKEN_ROUTES = new Set(['/verify-email', '/reset-password']);

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;
/** The path the mounted view was rendered for — see the popstate handler. */
let renderedPath: string | null = null;

export function navigate(to: string) {
  if (location.pathname !== to) history.pushState(null, '', to);
  render();
}

/**
 * When the client last asked the server whether this player is verified.
 *
 * The store's `user.emailVerified` is minted by `bootstrapSession()` at boot and
 * never updated afterwards, so it goes stale the moment somebody verifies — which
 * is ALWAYS, because verifying is the next thing a new player does. Both gates
 * below used to turn a player away on that stale copy:
 *
 *   register → /check-email → open the link → /verify-email → "verified, you can
 *   now sign in" → the anchor is same-origin, so the router navigates WITHOUT a
 *   page load → /login sees a signed-in user whose cached flag still says false →
 *   /check-email. Which offers exactly two buttons: resend, and sign out.
 *
 * A freshly verified player was therefore parked on "check your inbox" with no way
 * into the game but signing out and back in. It never showed up in dev because the
 * seeded accounts are created already verified.
 *
 * The fix is to stop trusting the cached flag when it says NO: ask the server, at
 * most once every few seconds. It cannot loop — each attempt stamps the clock, so
 * the re-render that follows falls through to the bounce.
 */
// −Infinity, not 0: the very first unverified render must ASK, and "0" only
// happens to do that because the clock is a large number. Say it outright.
let lastVerifyCheck = Number.NEGATIVE_INFINITY;

/**
 * True when the route may proceed. False means this pass has been handled: either
 * a re-check is in flight (which re-renders when it lands) or the player has been
 * sent to /check-email. The decision itself is in engine/verify-gate.ts, where it
 * can be tested.
 */
function requireVerified(user: { emailVerified: boolean }): boolean {
  switch (verifyDecision(user.emailVerified, Date.now(), lastVerifyCheck)) {
    case 'proceed':
      return true;
    case 'recheck':
      lastVerifyCheck = Date.now();
      void refreshSessionUser().then(() => render());
      return false;
    default:
      navigate('/check-email');
      return false;
  }
}

/**
 * Polls the session until the player reads as verified, then calls `onVerified`.
 *
 * Bounded: it stops after a couple of minutes so a screen left open overnight is
 * not still asking, and the returned teardown stops it the moment the view is
 * replaced. A signed-out visitor (an expired link, someone else's mail) reads as
 * "no session" and is left alone on the screen.
 */
function watchVerification(onVerified: () => void): () => void {
  const EVERY_MS = 3_000;
  const GIVE_UP_AFTER_MS = 2 * 60_000;
  const startedAt = Date.now();
  let stopped = false;

  const timer = window.setInterval(() => {
    if (stopped) return;
    if (Date.now() - startedAt > GIVE_UP_AFTER_MS) { stop(); return; }
    // Nobody signed in — an expired link, or somebody opening a mail that is not
    // theirs. Polling `/auth/me` there answers 401, which the API client turns
    // into a refresh attempt: forty of them over two minutes, against routes that
    // are deliberately throttled. There is nothing to watch for, so don't.
    if (!authStore.get().user) return;
    void refreshSessionUser().then((verified) => {
      if (stopped || !verified) return;
      stop();
      onVerified();
    });
  }, EVERY_MS);

  function stop() { stopped = true; window.clearInterval(timer); }
  return stop;
}

function render() {
  renderedPath = location.pathname;
  teardown?.();
  teardown = null;
  // The idle countdown belongs to the game view; leaving it re-arms only if the
  // next route is /play again.
  stopIdleWatch();

  const path = location.pathname;
  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  /** Core's teardown plus the Back button's, as one function. */
  const withAuthBack = (coreTeardown: (() => void) | void) => {
    const removeBack = attachAuthBack();
    return () => { removeBack(); if (typeof coreTeardown === 'function') coreTeardown(); };
  };

  const afterAuth = () => {
    const u = authStore.get().user;
    // This tab now owns the session. Without the mark, the very next boot would
    // treat the fresh cookies as a resumed-after-close session and sign the
    // player straight back out — see engine/session-policy.ts.
    markTabSession();
    if (u && !u.emailVerified) { navigate('/check-email'); return; }
    connectSocket();
    navigate('/play');
  };

  // The landscape gate covers every post-auth view and nothing before it.
  //
  // `/play` is both the game AND the character creator (the router picks between
  // them below), which is the case that was missing: the gate lived inside
  // mountGame, so a new player's first screen — the creator — was ungated. Driven
  // from here it cannot be: one owner, one gate, set on every render.
  //
  // Login, register and forgot-password stay upright on purpose. A sign-in form
  // works fine in a portrait column, and asking someone to rotate before they can
  // type their password is a worse greeting than the layout would ever be.
  setOrientationGate(path === '/play');

  // News and Guides: public pages, signed-in or not, and never a redirect —
  // a player reading a guide is not asking to be sent to their farm.
  const legacy = legacyArticleRedirect(path, location.search);
  if (legacy) { history.replaceState(null, '', legacy); render(); return; }
  const article = parseArticleRoute(path);
  if (article) {
    teardown = renderArticlesPage(app, navigate, article.section, article.slug);
    return;
  }

  switch (path) {
    // The auth screens come from core/, which a game repo must not edit. The Back
    // control is therefore attached AFTER core renders and its teardown composed
    // with core's, so leaving the screen removes it — see engine/auth-back.ts.
    case '/login':
      if (user) { if (requireVerified(user)) navigate('/play'); return; }
      teardown = withAuthBack(renderLogin(app, afterAuth));
      break;
    case '/register':
      if (user) { if (requireVerified(user)) navigate('/play'); return; }
      teardown = withAuthBack(renderRegister(app, afterAuth));
      break;
    case '/forgot-password':
      if (user) { navigate('/play'); return; }
      teardown = withAuthBack(renderForgotPassword(app));
      break;
    case '/reset-password':
      teardown = renderResetPassword(app, params.get('token') ?? '', () => navigate('/login'));
      break;
    // Legal pages are reachable signed-in OR signed-out and must never redirect:
    // the register screen links here from the consent checkbox in a new tab.
    case '/terms':
      teardown = renderLegal(app, 'terms');
      break;
    case '/privacy':
      teardown = renderLegal(app, 'privacy');
      break;
    // The marketing footer links all three CMS legal pages at /<slug>; without
    // this case /cookies fell through to the router's default and 404'd, which
    // is exactly the dead compliance link the other two cases exist to prevent.
    case '/cookies':
      teardown = renderLegal(app, 'cookies');
      break;
    case '/verify-email': {
      // Core's screen owns the POST and says "you can now sign in". For a player
      // who is ALREADY signed in — which is everyone arriving from the mail we
      // sent moments after registration — signing in again is not a step they
      // should have to take, so watch for the flag flipping and take them in.
      // Marking the tab here rather than at boot: a tab that has just spent the
      // token has proved control of the address, which is a better claim on the
      // session than "was open when you signed in". Without it a player who
      // verified in a mail-client tab would be sent to /login by the tab rule the
      // moment they moved.
      const stopWatch = watchVerification(() => { markTabSession(); navigate('/play'); });
      const coreTeardown = renderVerifyEmail(app, params.get('token') ?? '');
      teardown = () => { stopWatch(); if (typeof coreTeardown === 'function') coreTeardown(); };
      break;
    }
    case '/check-email': {
      if (!user) { navigate('/login'); return; }
      // The link is usually opened somewhere else — the mail app's own browser, a
      // second tab, a phone. This screen has no way of hearing about that, so it
      // asks; otherwise the player comes back to a tab still insisting they check
      // their inbox for a mail they have already acted on.
      const stopWatch = watchVerification(() => navigate('/play'));
      const coreTeardown = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate('/login'); });
      teardown = () => { stopWatch(); if (typeof coreTeardown === 'function') coreTeardown(); };
      break;
    }
    case '/':
      // A bare "/" for a signed-in player is "take me to my farm". A "/#faq" is not:
      // News/Guides/Legal link back into the landing's sections, and bouncing those
      // to /play made every one of those links open the game instead.
      if (user && !window.location.hash) { navigate('/play'); return; }
      teardown = renderLanding(app, navigate);
      break;
    case '/shop': {
      if (!user) { navigate('/login'); return; }
      const payment = params.get('payment');
      if (payment === 'success') toast('Payment successful!', 'ok');
      else if (payment === 'cancelled') toast('Payment was cancelled', 'error');
      writeLastTab('shop');
      navigate('/play');
      return;
    }
    case '/play':
      if (!user) { navigate('/login'); return; }
      if (!requireVerified(user)) return;
      connectSocket();
      // Ten minutes idle ends the session (session-policy.ts). Restarted on every
      // entry to /play; torn down by the router when the view is replaced.
      startIdleWatch();
      // Scope the tutorial to THIS player before anything reads it. The flags used
      // to be shared by every account that ever signed in on the browser, so the
      // second account on a machine was dropped straight into the real map with no
      // creator, no glade and no tour. Same key convention as the appearance store
      // on the next line.
      setTutorialAccount(user.email);
      // A hero an operator applied from the admin player card. Checked in the
      // background so entering the game never waits on it; when it does change
      // the look, the route renders again to show it.
      void syncAssignedHero(user.email, () => accountApi.hero()).then((changed) => {
        if (changed && location.pathname === '/play') render();
      });
      if (!loadAppearance(user.email)) {
        teardown = renderCharacterCreator(app, user.username ?? user.email, (a) => { saveAppearance(user.email, a); render(); });
      } else {
        teardown = mountGame(app, user, () => { logout(); disconnectSocket(); navigate('/login'); });
      }
      break;
    default:
      navigate('/');
  }
}

/**
 * Scroll to `location.hash`'s target, if it names one that exists.
 *
 * The offset is applied here rather than with `scroll-padding-top`, which never
 * took effect on this page, so a target would otherwise land underneath the
 * sticky header.
 */
function scrollToHash(): void {
  const id = location.hash.slice(1);
  if (!id) return;
  let el: HTMLElement | null = null;
  try { el = document.getElementById(decodeURIComponent(id)); } catch { return; }
  if (!el) return;

  const header = document.querySelector('.topbar, header');
  // 8px of air so the heading does not sit flush against the header's edge.
  const offset = (header ? header.getBoundingClientRect().height : 0) + 8;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  const top = Math.max(0, Math.min(max, el.getBoundingClientRect().top + window.scrollY - offset));
  // site.css already asks for `scroll-behavior: smooth` on html, so this animates.
  window.scrollTo({ top });
}

window.addEventListener('popstate', () => {
  if (renderedPath !== null && location.pathname === renderedPath) { scrollToHash(); return; }
  render();
});

/**
 * Same-origin links are routed in-app instead of reloading the page.
 *
 * Two kinds of link must NOT be swallowed by that, and both were:
 *
 *  - In-page anchors. The landing page's header menu is `#world`, `#craft`,
 *    `#faq` and friends. This handler called `preventDefault()` on them and then
 *    navigated to `pathname + search` — i.e. it threw the hash away, re-rendered
 *    the whole landing page and left the reader back at the top. That is the
 *    "anchors on the landing page don't work" report: the link was fine, the
 *    router ate it.
 *  - Downloads and non-http(s) links. An `<a download href="blob:...">` is how a
 *    generated file (the account statement PDF) reaches the disk; mailto:/tel:
 *    belong to another app. Routing those threw the page away and re-rendered it,
 *    which is the "statement PDF reloads the game" report.
 *  - Modifier and middle clicks. Cmd/Ctrl-click to open in a new tab, or
 *    shift-click for a new window, were being turned into an in-place navigation
 *    like any other click.
 *
 * Everything else still routes without a page load, which is the point.
 */
document.addEventListener('click', (e) => {
  const a = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.origin !== location.origin) return;
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  // Downloads and non-http(s) schemes are the browser's job, never the router's.
  if (isBrowserHandledLink(a)) return;
  // A link into THIS page: let the browser scroll and set the hash itself.
  // `href="#"` has an empty `hash`, so the Play buttons still route.
  if (a.hash && a.pathname === location.pathname) return;
  e.preventDefault();
  navigate(a.pathname + a.search + a.hash);
  // A section link from another page (`/#faq` on /news): the landing is mounted
  // now, so take the reader to the section rather than the top of the page.
  if (a.hash) scrollToHash();
});

(async () => {
  // Fetch site-info first — drives preview-mode gating throughout the app
  await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();

  // Before the first render: canvas text is rasterised once and never redrawn, so
  // the face has to be in memory by then — see engine/fonts.ts.
  await loadGameFonts();

  await bootstrapSession();
  // The refresh cookie outlives the tab by three days, and bootstrapSession spends
  // it unconditionally — which is why closing the browser and returning hours later
  // landed straight in the farm. A session the cookies would resume in a tab that
  // never opened it is signed out here instead, server-side token included.
  //
  // EXCEPT on an email-token route. This check returns before `render()`, so
  // applying it there meant the verify screen never mounted and the token was
  // never spent: the player clicked the link, got bounced to /login, and stayed
  // unverified for ever. A mail client opens links in a NEW tab, and a new tab
  // never owns the session, so this was every player, every time.
  const fromEmailLink = EMAIL_TOKEN_ROUTES.has(location.pathname);
  if (!fromEmailLink && await enforceTabSession()) return;
  if (!fromEmailLink && authStore.get().user) markTabSession();
  render();
  // A shared deep link (`/#craft`) lands before the page it points into exists —
  // the browser looks for the target while #app is still the loading spinner and
  // finds nothing, so the reader arrives at the top of the page instead of at the
  // section they were sent to. Now that the view is mounted, honour it.
  scrollToHash();

  authStore.subscribe((s) => {
    if (!s.user && location.pathname === '/play') navigate('/');
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  unmountGame();
});
